import _ from 'lodash';
import ROOT from '../../../inversify.config';
import { CardsLibraryToken } from "./CardsLibraryToken";
import { getAllToken } from 'inversify-token';

const repeat = {
  "title": "Repeat",
  "description": "The number of times this component is repeated in order.",
  "type": "integer",
  "minimum": 1
};

const padding = {
  "title": "Padding",
  "type": "object",
  "properties": {
    "top": {
      "type": "string",
      "title": "Top",
      "description": "Top padding in standard CSS units."
    },
    "right": {
      "type": "string",
      "title": "Right",
      "description": "Right padding in standard CSS units."
    },
    "bottom": {
      "type": "string",
      "title": "Bottom",
      "description": "Bottom padding in standard CSS units."
    },
    "left": {
      "type": "string",
      "title": "Left",
      "description": "Left padding in standard CSS units."
    }
  }
};

const margin = {
  "title": "Margin",
  "type": "object",
  "properties": {
    "top": {
      "type": "string",
      "title": "Top",
      "description": "Top margin in standard CSS units. Negative values are allowed."
    },
    "right": {
      "type": "string",
      "title": "Right",
      "description": "Right margin in standard CSS units. Negative values are allowed."
    },
    "bottom": {
      "type": "string",
      "title": "Bottom",
      "description": "Bottom margin in standard CSS units. Negative values are allowed."
    },
    "left": {
      "type": "string",
      "title": "Left",
      "description": "Left margin in standard CSS units. Negative values are allowed."
    }
  }
};

const border = {
  "title": "Border",
  "type": "object",
  "properties": {
    "top": {
      "type": "string",
      "title": "Top",
      "description": "Top border in standard CSS units."
    },
    "right": {
      "type": "string",
      "title": "Right",
      "description": "Right border in standard CSS units."
    },
    "bottom": {
      "type": "string",
      "title": "Bottom",
      "description": "Bottom border in standard CSS units."
    },
    "left": {
      "type": "string",
      "title": "Left",
      "description": "Left border in standard CSS units."
    },
    "radius": {
      "type": "string",
      "title": "Radius",
      "description": "Border radius in standard CSS units."
    }
  }
};

const background = {
  "title": "Background",
  "type": "object",
  "properties": {
    "color": {
      "title": "Color",
      "description": "A solid background color. Will be overridden if an image is specified.",
      "type": "string",
      "format": "color"
    },
    "url": {
      "type": "string",
      "format": "url",
      "title": "Image",
      "description": "The image for the background. This takes precedence over color.",
      "options": { "upload": {} },
      "links": [ { "href": "{{self}}" } ]
    },
    "size": {
      "type": "string",
      "title": "Size",
      "description": "Cover: The image will be resized to fill the area and cropped to maintain aspect ratio. Contain: The image will be scaled up or down so that the entire image fits within the area, maintaining aspect ratio.",
      "enum": [
        "COVER",
        "CONTAIN"
      ],
      "options": {
        "enum_titles": [
          "Cover",
          "Contain"
        ]
      }
    },
    "attachment": {
      "type": "string",
      "title": "Attachment",
      "description": "Fixed: The image will remain always in the same position on the screen. Local: The background image will scroll with the content. Scroll: The background image will scroll with the content's container.",
      "enum": [
        "FIXED",
        "LOCAL",
        "SCROLL"
      ],
      "options": {
        "enum_titles": [
          "Fixed",
          "Local",
          "Scroll"
        ]
      }
    },
    "position": {
      "type": "string",
      "title": "Position",
      "description": "The anchor point of the background for cropping. A combination of (top, center, bottom) and (left, center, right). For example, use \"center center\" to center crop.",
    },
    "repeat": {
      "type": "string",
      "title": "repeat",
      "description": "Repeat: Enables repeating the background to cover the target area. No Repeat: Disables repeating the background (default).",
      "enum": [
        "REPEAT",
        "REPEAT-X",
        "REPEAT-Y",
        "NO-REPEAT",
        "SPACE",
        "ROUND"
      ],
      "options": {
        "enum_titles": [
          "Repeat",
          "Repeat left-to-right only",
          "Repeat top-to-bottom only",
          "No Repeat",
          "Space",
          "Round"
        ]
      }
    }
  }
};

const conditions = {
  "title": "Conditions",
  "type": "object",
  "properties": {
    "isNative": {
      "type": "boolean",
      "title": "App Only?",
      "description": "Only show this component when displayed within a native mobile app."
    },
    "isDesktop": {
      "type": "boolean",
      "title": "Web Only?",
      "description": "Only show this component when displayed on a website inside a browser."
    },
    "not": {
      "type": "boolean",
      "title": "Invert Logic",
      "description": "Show this component when these conditions are NOT met."
    }
  }
};

export const CARDS_ELEMENT_SCHEMA = {
  "title": " ",
  "type": "object",
  "properties": {
    "class": {
      "type": "string",
      "title": "Element Type",
      "description": "The type of element to place in this position.",
      "enum": [
      ],
      "options": {
        "enum_titles": [
        ]
      }
    },
    repeat,
    "data": {
      "title": "Type-Specific Data",
      "type": "object",
      "description": "Additional data specific to the element type.",
      "properties": {
      },
      "additionalProperties": true
    },
    "span": {
      "title": "Row/Column Span",
      "type": "object",
      "description": "When placed in an array, specifies how many rows or columns this element should span. Defaults to 1 for both rows and columns.",
      "properties": {
        "rows": {
          "type": "integer",
          "description": "The number of rows this element should span. Defaults to 1.",
          "minimum": 1
        },
        "columns": {
          "type": "integer",
          "description": "The number of columns this element should span. Defaults to 1.",
          "minimum": 1
        }
      }
    },
    "sourceIds": {
      "title": "Data Sources",
      "type": "array",
      "description": "The IDs of data sources that this card uses.",
      "items": {
        "type": "string"
      }
    },
    padding,
    border,
    background,
    conditions
  },
  "required": [
    "class"
  ]
};

const cardLibraryRecords = getAllToken(ROOT, CardsLibraryToken);
const classNamePairs: [string, string][] = [];
for (let rec of cardLibraryRecords) {
  const clazz = rec.clazz;
  const name = (_.isFunction(rec.getDisplayName))? rec.getDisplayName() : clazz;
  classNamePairs.push([clazz, name]);
}
classNamePairs.sort((a, b) => a[1].localeCompare(b[1]));
for (let pair of classNamePairs) {
  CARDS_ELEMENT_SCHEMA.properties.class.enum.push(pair[0]);
  CARDS_ELEMENT_SCHEMA.properties.class.options.enum_titles.push(pair[1]);
}

export const CARDS_ARRAY_LAYOUT_SCHEMA = {
  "title": " ",
  "type": "object",
  "properties": {
    "breakpoint": {
      "type": "integer",
      "title": "Breakpoint (pixels)",
      "description": "The minimum screen width in pixels to show this layout.",
      "default": 0
    },
    "type": {
      "type": "string",
      "title": "Grouping Style",
      "description": "How the cards elements will be grouped on the page. Single: A single full-wdith element. Grid: A grid of elements. Carousel: A horizontally scrolling carousel.",
      "enum": [
        "GRID",
        "SINGLE",
        "SWIPER"
      ],
      "options": {
        "enum_titles": [
          "Grid",
          "Single",
          "Carousel"
        ]  
      }
    },
    "maxColumns": {
      "type": "integer",
      "minimum": 1,
      "title": "Maximum Columns",
      "description": "The maximum number of columns visible on screen in a carousel or grid layout."
    },
    "gapPixels": {
      "type": "number",
      "title": "Element Gap (px)",
      "description": "Gap between elements in pixels."
    },
    "splitEqually": {
      "type": "boolean",
      "title": "Split Equally",
      "description": "In a grid layout, this forces the number of columns to be equal to the number of elements. If this is true, maxColumns is ignored."
    },
    "scrollSpeed": {
      "type": "number",
      "title": "Scroll Speed",
      "description": "The scrolling speed of a carousel in pixels per second. Default is 300."
    },
    "slideWidth": {
      "type": "string",
      "title": "Slide Width",
      "description": "The width of each slide in a carousel in standard CSS units."
    },
    "slideSnap": {
      "type": "boolean",
      "title": "Slide Snap",
      "description": "Should slides in a carousel snap into position (like a pager)?",
    },
    "swipeEffect": {
      "type": "string",
      "title": "Carousel Animation",
      "description": "The animation used when swiping between items in a carousel. The default sliding animiation is \"Slide\".",
      "enum": [
        "SLIDE",
        "FADE",
        "CUBE",
        "COVERFLOW",
        "FLIP"
      ],
      "options": {
        "enum_titles": [
          "Slide",
          "Fade",
          "Cube",
          "Coverflow",
          "Flip"
        ]
      }
    },
    "swiperPagination": {
      "type": "boolean",
      "title": "Carousel Pagination",
      "description": "Whether pagination dots are enabled on carousels.",
    },
    "swiperNavigation": {
      "type": "boolean",
      "title": "Carousel Navigation",
      "description": "Whether navigation arrows are enabled on carousels.",
    },
    "scrollToActive": {
      "type": "boolean",
      "title": "Scroll to Active",
      "description": "Whether a carousel should automatically scroll to the first active element (for example, an upcoming or live match). This requires the elements within the carousel to support that functionality.",
    },
    padding,
    margin,
    background
  },
  "required": [
    "breakpoint",
    "type"
  ]
};

export const CARDS_COLUMN_SCHEMA = {
  "title": " ",
  "type": "object",
  "properties": {
    padding,
    margin,
    "width": {
      "type": "integer",
      "minimum": 0,
      "maximum": 100,
      "multipleOf": 1,
      "title": "Width (percent)",
      "description": "The percentage of the width of the parent section that this column will occupy. This can be left unspecified or set to 0, in which case this column will be automatically sized based on the size of its content."
    },
    "grow": {
      "type": "boolean",
      "title": "Allow Grow To Fit Content",
      "description": "Whether the width of this column will be allowed to grow beyond the width specified to fit the content. Ignored if width is unspecified or zero."
    },
    "shrink": {
      "type": "boolean",
      "title": "Allow Shrink To Fit Content",
      "description": "Whether the width of this column will be allowed to shrink below the width specified to tightly fit the content. Ignored if width is unspecified or zero."
    },
  },
  "required": [
  ]
};

export const CARDS_SECTION_SCHEMA = {
  "title": " ",
  "type": "object",
  "properties": {
    padding,
    margin,
    background,
    "height": {
      "type": "integer",
      "minimum": 0,
      "title": "Fixed Height",
      "description": "Specifies a fixed height for the section in standard CSS units. If a fixed height is specified and the section's content is larger than the height specified (vertically), the section will scroll."
    },
    "autoscroll": {
      "type": "boolean",
      "title": "Auto Scroll",
      "description": "Auto-scroll the section to the first active element (for example, an upcoming or live match). Auto-scroll must be supported by elements within this section."
    }
  },
  "required": [
  ]
};

export const CARDS_TAB_SCHEMA = {
  "title": " ",
  "type": "object",
  "properties": {    
    "default": {
      "type": "boolean",
      "title": "Is Default Tab?",
      "description": "If set to true, this tab will be the first tab shown on page load within this region.",
      "default": true
    },
    padding,
    margin,
    background,    
    "sources": {
      "type": "array",
      "title": "Data Sources",
      "description": "Definition of data sources that can be used by elements within this tab.",
      "items": {
        "title": "Data Source",
        "description": "A data source that can be used by elements within this tab.",
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "title": "Identifier (ID)",
            "description": "A unique identifier for this data source that is referenced by card elements."
          },
          "class": {
            "type": "string",
            "title": "Data Source Type",
            "description": "The type of the data source (must match a valid data source in the code)."
          },
          "path": {
            "type": "string",
            "title": "API Path",
            "description": "The path of the data source's API."
          },
          "name": {
            "type": "string",
            "title": "Name",
            "description": "A user-friendly name for the data source."
          },
          "data": {
            "type": "object",
            "title": "Source-Specific Data",
            "description": "Configuration data for the source based on the source's type."
          }
        },
        "required": [
          "id",
          "class"
        ]
      }
    },
    /*
    "fragmentId": {
      "type": "string",
      "description": "If specified, this fragment ID will be used to display the contents of the tab."
    }
    */
  }
};

export const CARDS_PAGE_SCHEMA = {
  "title": " ",
  "type": "object",
  "properties": {
    "analytics": {
      "type": "object",
      "title": "Analytics",
      "description": "Configuration of analytics tracking data for this page.",
      "properties": {
        "path": {
          "type": "string",
          "title": "Path",
          "description": "The page path to report for analytics tracking."
        },
        "name": {
          "type": "string",
          "title": "Name",
          "description": "The page name to report for analytics tracking."
        }
      },
      "required": [
        "path",
        "name"
      ]
    },
    "defaultParameters": {
      "type": "object",
      "title": "Default Parameters",
      "description": "Default parameters for this page.",
      "patternProperties": {
        "^.+$": { 
          "type": "string"
        }
      },
      "additionalProperties": true,
      "options": {
        "disable_properties": false
      }
    }
  }
};